import {LitElement, html} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import {DataManagementService} from '../../service/datamanagement/dataManagementService.js';
import {ElementUtil} from '../../utils/elementUtil.js';
import {TimeUtil} from '../../utils/timeUtil.js';
import {ExportUtil} from '../../utils/exportUtil.js';
import {BaseComponent} from '../basecomponent.js';
import {showMigrationErrorDialog} from './migrationErrorDialog.js';
import {showMigrationStatusDialog} from './migrationStatusDialog.js';
import {showMigrationStep2Dialog} from './migrationStep2Dialog.js';
import {showMigrationStep3Dialog} from './migrationStep3Dialog.js';
import {showMigrationCompanyDetailsDialog} from './migrationCompanyDetailsDialog.js';
import {showMigrationEditDialog} from './migrationEditDialog.js';
import {showMigrationSyncHistoryDialog} from './migrationSyncHistoryDialog.js';
import {
	buildMigrationOptionFields,
	canEditMigration,
	canRetryMigration,
	isStuckPending,
	isStuckWorking
} from './migrationOptionFields.js';

class MigrationGrid extends BaseComponent {

	static get properties() {
		return {
			admin: {type: Boolean}
		};
	}

	constructor() {
		super();
		this.admin = true;
		var migrationGrid = this;
		setTimeout(function() {
			migrationGrid.init();
		});
	}

	render() {
		return html`<slot></slot>`;
	}

	init() {
		var migrationGrid = this;
		var migrationService = DataManagementService.getMigrationService();
		var grid;
		var pagingToolbar;

		var toolbarFilter = ElementUtil.create({
			tag: 'dl-toolbar',
		'collapse-label': 'Filter',
			slot: 'toolbar',
			theme: 'spacing',
			children: [
				{
					tag: 'vaadin-combo-box',
					label: 'Filter Field',
					itemid: 'filterField',
					'class': 'spacer-toolbar-item',
					'item-value-path': 'code',
					'item-label-path': 'description',
					required: true,
					value: 'v6CompanyName',
					postinit: function(combo) {
						combo.items = [
							{code: 'v6CompanyName', description: 'V6 Company Name'},
							{code: 'v6CompanyId', description: 'V6 Company Id'}
						];
					}
				},
				{
					tag: 'vaadin-text-field',
					itemid: 'filterValue',
					'class': 'spacer-toolbar-item',
					'keep-open': true,
					label: 'Filter By',
					listeners: {
						input: {
							fn: function() {
								pagingToolbar.loadPage(1);
							},
							buffer: 2000
						}
					}
				},
				{
					tag: 'vaadin-combo-box',
					label: 'Step',
					itemid: 'filterStep',
					'class': 'spacer-toolbar-item',
					'item-value-path': 'code',
					'item-label-path': 'description',
					'clear-button-visible': true,
					'keep-open': true,
					placeHolder: 'All',
					postinit: function(combo) {
						combo.items = [
							{code: 'INITIAL', description: 'Step 1 - Initial Migration'},
							{code: 'VALIDATION', description: 'Step 2 - Validation'},
							{code: 'FINALIZATION', description: 'Step 3 - Finalization'},
							{code: 'COMPLETE', description: 'Complete'}
						];
					},
					listeners: {
						'selected-item-changed': function() {
							if (pagingToolbar) {
								pagingToolbar.loadPage(1);
							}
						}
					}
				}
			]
		});

		var toolbar = ElementUtil.create({
			tag: 'dl-toolbar',
			slot: 'toolbar',
			children: [
				{
					tag: 'dl-action-button',
					itemid: 'refresh',
					'class': 'spacer-toolbar-item',
					children: [ElementUtil.makeIcon('vaadin:refresh', ' Refresh')],
					permissions: ['ADMIN_DATA_MIGRATION:READ'],
					listeners: {click: actionRefresh}
				},
				{
					tag: 'dl-action-button',
					itemid: 'add',
					'class': 'spacer-toolbar-item',
					children: [ElementUtil.makeIcon('vaadin:plus', ' New Migration')],
					permissions: ['ADMIN_DATA_MIGRATION:WRITE'],
					listeners: {click: actionAdd}
				},
				{
					tag: 'dl-action-button',
					itemid: 'edit',
					'class': 'spacer-toolbar-item',
					children: [ElementUtil.makeIcon('vaadin:edit', ' Edit')],
					disabled: true,
					permissions: ['ADMIN_DATA_MIGRATION:WRITE'],
					listeners: {click: actionEdit}
				},
				{
					tag: 'dl-action-button',
					itemid: 'status',
					'class': 'spacer-toolbar-item',
					children: [ElementUtil.makeIcon('vaadin:tasks', ' Migration Status')],
					disabled: true,
					permissions: ['ADMIN_DATA_MIGRATION:READ'],
					listeners: {click: actionMigrationStatus}
				},
				{
					tag: 'dl-action-button',
					itemid: 'retry',
					'class': 'spacer-toolbar-item',
					children: [ElementUtil.makeIcon('vaadin:rotate-right', ' Retry')],
					disabled: true,
					permissions: ['ADMIN_DATA_MIGRATION:WRITE'],
					listeners: {click: actionRetry}
				},
				{
					tag: 'dl-action-button',
					itemid: 'syncHistory',
					'class': 'spacer-toolbar-item',
					children: [ElementUtil.makeIcon('vaadin:clock', ' Sync History')],
					disabled: true,
					permissions: ['ADMIN_DATA_MIGRATION:READ'],
					listeners: {click: actionSyncHistory}
				}
			]
		});

		grid = ElementUtil.create({
			tag: 'vaadin-grid',
			theme: 'row-stripes column-borders',
			'column-reordering-allowed': '',
			style: 'height: 100%;',
			slot: 'primary',
			defaults: {
				tag: 'vaadin-grid-sort-column',
				resizable: true
			},
			children: [
				{tag: 'vaadin-grid-sort-column', path: 'v6CompanyId', header: 'V6 Company Id', autoWidth: true},
				{tag: 'vaadin-grid-sort-column', path: 'v6CompanyName', header: 'V6 Company', autoWidth: true},
				{tag: 'vaadin-grid-sort-column', path: 'companyName', header: 'V8 Company', autoWidth: true},
				{tag: 'vaadin-grid-sort-column', path: 'migrationPhaseDescription', header: 'Step', autoWidth: true,
					renderer: function(root, column, rowData) {
						var phase = rowData.item.migrationPhase;
						if (phase === 'VALIDATION') {
							root.style.backgroundColor = '#66b3ff';
							root.style.color = 'black';
						} else if (phase === 'FINALIZATION') {
							root.style.backgroundColor = '#669999';
							root.style.color = 'black';
						} else {
							root.style.backgroundColor = '';
							root.style.color = '';
						}
						root.textContent = rowData.item.migrationPhaseDescription || '';
					}
				},
				{tag: 'vaadin-grid-sort-column', path: 'runStatusDescription', header: 'Status', autoWidth: true,
					renderer: function(root, column, rowData) {
						var status = rowData.item.runStatusDescription || '';
						if (rowData.item.runStatus === 'ERROR' || isStuckWorking(rowData.item) || isStuckPending(rowData.item)) {
							var suffix = isStuckWorking(rowData.item) || isStuckPending(rowData.item) ? ' (retry available)' : '';
							root.innerHTML = '<span style="color: #d32f2f;">' + status + suffix + '</span>';
						} else {
							root.textContent = status;
						}
					}
				},
				{tag: 'vaadin-grid-sort-column', path: 'dataStartDate', header: 'Start', autoWidth: true,
					renderer: function(root, column, rowData) {
						if (rowData.item.dataStartDate) {
							var date = TimeUtil.parseDate(rowData.item.dataStartDate);
							root.innerHTML = TimeUtil.formatDateStandard(date);
						} else {
							root.innerHTML = 'N/A';
						}
					}
				},
				{tag: 'vaadin-grid-sort-column', path: 'dataEndDate', header: 'End', autoWidth: true,
					renderer: function(root, column, rowData) {
						if (rowData.item.dataEndDate) {
							var date = TimeUtil.parseDate(rowData.item.dataEndDate);
							root.innerHTML = TimeUtil.formatDateStandard(date);
						} else {
							root.innerHTML = 'N/A';
						}
					}
				}
			],
			listeners: {
				'active-item-changed': function(event) {
					var selected = event.detail.value;
					grid.selectedItems = selected ? [selected] : [];
					ElementUtil.query(toolbar, 'status').disabled = !selected;
					ElementUtil.query(toolbar, 'retry').disabled = !selected || !canRetryMigration(selected);
					ElementUtil.query(toolbar, 'edit').disabled = !selected || !canEditMigration(selected);
					ElementUtil.query(toolbar, 'syncHistory').disabled = !selected;
				}
			}
		});

		pagingToolbar = ElementUtil.create({
			tag: 'dl-paging-toolbar',
			slot: 'footer',
			pageSize: 50,
			postinit: function(element) {
				element.grid = grid;
				element.loader = function(backingGrid, pageNumber, pagingToolbarRef) {
					var pageRange = pagingToolbarRef.pullRange();
					var loadMask = grid.showLoading();

					grid.currentFilter = {
						offset: pageRange.offset,
						max: pageRange.max,
						sortField: backingGrid.currentSortField ? backingGrid.currentSortField : 'createDts',
						sortOrder: backingGrid.currentSortDirection ? backingGrid.currentSortDirection : 'DESC',
						dateField: 'createDts',
						filterField: ElementUtil.query(toolbarFilter, 'filterField').value,
						filterValue: ElementUtil.query(toolbarFilter, 'filterValue').value,
						migrationPhase: ElementUtil.query(toolbarFilter, 'filterStep').value
					};

					migrationService.getMigrations(grid.currentFilter).then(function(records) {
						loadMask.remove();
						grid.activeItem = null;
						grid.items = records.data || [];
						pagingToolbarRef.totalRecords = records.totalNumber;
						pagingToolbarRef.updatePager();
					});
				};
			},
			listeners: {
				ready: function() {
					pagingToolbar.loadPage(1);
				}
			}
		});

		var footer = ExportUtil.createExportFooter({
			pagingToolbar: pagingToolbar,
			grid: grid,
			itemid: 'primary',
			onDetail: function() {
				var exportFilter = Object.assign({}, grid.currentFilter || {});
				delete exportFilter.offset;
				delete exportFilter.max;
				migrationService.exportMigrations(exportFilter);
			}
		});

		var toolbarContainer = ElementUtil.create({
			tag: 'div',
			slot: 'toolbar',
			children: [
				toolbarFilter,
				ElementUtil.create({tag: 'hr'}),
				toolbar
			]
		});

		var layout = ElementUtil.create({
			tag: 'dl-flex-layout',
			children: [toolbarContainer, grid, footer.footerContainer]
		});
		migrationGrid.appendChild(layout);

		function actionRefresh() {
			pagingToolbar.loadPage(1);
		}

		function actionEdit() {
			var record = grid.activeItem;
			if (!record || !canEditMigration(record)) {
				return;
			}
			migrationService.getMigration(record.migrationId).then(function(fullRecord) {
				var migration = fullRecord.data || fullRecord;
				showMigrationEditDialog(migration, migrationService, actionRefresh);
			});
		}

		function actionSyncHistory() {
			var record = grid.activeItem;
			if (!record) {
				return;
			}
			migrationService.getMigration(record.migrationId).then(function(fullRecord) {
				var migration = fullRecord.data || fullRecord;
				showMigrationSyncHistoryDialog(migration, migrationService, actionRefresh);
			});
		}

		function actionMigrationStatus() {
			var record = grid.activeItem;
			if (!record) {
				return;
			}
			openMigrationDialog(record);
		}

		function actionRetry() {
			var record = grid.activeItem;
			if (!record || !canRetryMigration(record)) {
				return;
			}
			migrationService.getMigration(record.migrationId).then(function(fullRecord) {
				var migration = fullRecord.data || fullRecord;
				showMigrationErrorDialog(migration, migrationService, actionRefresh);
			});
		}

		function openMigrationDialog(record) {
			migrationService.getMigration(record.migrationId).then(function(fullRecord) {
				var migration = fullRecord.data || fullRecord;
				if (migration.runStatus === 'ERROR' || isStuckWorking(migration) || isStuckPending(migration)) {
					showMigrationErrorDialog(migration, migrationService, actionRefresh);
					return;
				}
				if (migration.migrationPhase === 'VALIDATION') {
					showMigrationStep2Dialog(migration, migrationService, actionRefresh);
					return;
				}
				if (migration.migrationPhase === 'FINALIZATION') {
					showMigrationStep3Dialog(migration, migrationService, actionRefresh);
					return;
				}
				showMigrationStatusDialog(migration, migrationService, actionRefresh);
			});
		}

		function resolveCompanySearchParams(filter) {
			if (!filter) {
				return null;
			}
			var trimmed = filter.trim();
			if (trimmed.length < 2) {
				return null;
			}
			if (/^\d+$/.test(trimmed)) {
				return {companyId: trimmed};
			}
			if (/^[A-Za-z]{2,5}$/.test(trimmed)) {
				return {companyType: trimmed.toUpperCase()};
			}
			return {companyName: trimmed};
		}

		function actionAdd() {
			var addWin = ElementUtil.create({
				tag: 'dl-dialog',
				title: 'New Migration',
				icon: 'vaadin:exchange',
				width: .75,
				height: .75,
				listeners: {
					ready: function() {
						addWin.show();

						var optionFields = buildMigrationOptionFields();

						var formPanel = ElementUtil.create({
							tag: 'dl-form-panel',
							itemid: 'form',
							children: [{
								tag: 'vaadin-form-layout',
								children: [
									{
										tag: 'div',
										colspan: 2,
										style: 'width: 100%; display: flex; align-items: flex-end; gap: var(--lumo-space-s); box-sizing: border-box;',
										children: [
											{
												tag: 'vaadin-combo-box',
												name: 'v6CompanyId',
												label: 'V6 Company',
												required: true,
												style: 'flex: 1; min-width: 0;',
												'item-label-path': 'description',
												'item-value-path': 'v6CompanyId',
												placeholder: 'Search by V6 id, name, or type',
												postinit: function(combo) {
													combo.pageSize = 50;
													var companySearchTimeout;
													var companySearchRequestId = 0;
													combo.dataProvider = function(params, callback) {
														var searchParams = resolveCompanySearchParams(params.filter);
														if (!searchParams) {
															callback([], 0);
															return;
														}
														if (companySearchTimeout) {
															clearTimeout(companySearchTimeout);
														}
														var requestId = ++companySearchRequestId;
														companySearchTimeout = setTimeout(function() {
															migrationService.searchV6Companies(searchParams).then(function(records) {
																if (requestId !== companySearchRequestId) {
																	return;
																}
																var items = records.data || [];
																callback(items, records.totalNumber || items.length);
															});
														}, 2000);
													};
												},
												listeners: {
													'value-changed': function(event) {
														var viewDetailsButton = ElementUtil.query(formPanel, 'viewDetails');
														if (viewDetailsButton) {
															viewDetailsButton.disabled = !event.detail.value;
														}
													}
												}
											},
											{
												tag: 'vaadin-button',
												itemid: 'viewDetails',
												theme: 'tertiary',
												disabled: true,
												style: 'flex-shrink: 0; margin-bottom: var(--lumo-space-xs);',
												children: [ElementUtil.makeIcon('vaadin:list', ' View Details')],
												listeners: {
													click: function() {
														var combo = ElementUtil.query(formPanel, 'vaadin-combo-box');
														if (!combo || !combo.value) {
															return;
														}
														var companyName = combo.selectedItem
															? combo.selectedItem.companyName
															: combo.value;
														showMigrationCompanyDetailsDialog(
															combo.value,
															companyName,
															migrationService
														);
													}
												}
											}
										]
									}
								].concat(optionFields.fields).concat([
									{tag: 'hr', colspan: 2},
									{
										tag: 'div',
										style: 'text-align: center;',
										colspan: 2,
										defaults: {tag: 'vaadin-button'},
										children: [
											{
												theme: 'primary',
												children: [ElementUtil.makeIcon('vaadin:database', ' Start Migration')],
												listeners: {
													click: function() {
														formPanel.submitForm({
															preSubmit: function(data) {
																data.migrationType = 'INITIAL';
																data.pullAllData = !!data.pullAllData;
																data.cleanupInactiveUnits = !!data.cleanupInactiveUnits;
																data.allowArchiveOnNextRun = !!data.allowArchiveOnNextRun;
																var combo = ElementUtil.query(formPanel, 'vaadin-combo-box');
																if (combo.selectedItem) {
																	data.v6CompanyName = combo.selectedItem.companyName;
																}
																if (data.pullAllData) {
																	// Server applies the default 5-year range from today.
																	data.dataStartDate = null;
																	data.dataEndDate = null;
																}
																return data;
															},
															actualCall: function(data) {
																return migrationService.createMigration(data);
															},
															success: function() {
																window.DL.MessageUtil.showToast('Success', 'Migration submitted', {
																	theme: 'success',
																	position: 'top-stretch'
																});
																actionRefresh();
																addWin.close();
															}
														});
													}
												}
											},
											{
												theme: 'tertiary',
												innerHTML: 'Cancel',
												style: 'margin-left: 10px;',
												listeners: {
													click: function() {
														addWin.close();
													}
												}
											}
										]
									}
								])
							}]
						});

						addWin.addToContentElement(formPanel);
					}
				}
			});
		}
	}
}

customElements.define('dl-migration-grid', MigrationGrid);

export {MigrationGrid};
